import * as React from 'react';
import Modal, { Campo, classesEntrada, BotaoPrimario, BotaoSecundario } from './Modal';

/* ============================================================================
   ModalAplicativoPolitica - editar o vinculo de UM aplicativo dentro da politica.

   Substitui `pages/dashboard/politicas/components/modal/index.tsx` (MUI + Dialog
   + react-hook-form + Yup vazio + Autocomplete) por um dialogo do visual novo.

   O que este modal decide de fato sao DOIS campos: tipo de instalacao e modo de
   atualizacao. Nome e pacote sao mostrados porque identificam a linha, e ficam
   somente-leitura - trocar o pacote aqui nao faria sentido: o vinculo E o pacote.

   TRES DEFEITOS DO MODAL ANTIGO QUE NAO SAO REPRODUZIDOS:

   1. Ele devolvia `{...data, installType, autoUpdateMode}` onde `data` vinha do
      formulario e tinha SO `name` e `packageName`. Resultado: `codigo` e a marca
      `novo: true` eram perdidos ao editar. Um aplicativo recem-adicionado que o
      usuario editasse antes de salvar deixava de ser contado como novo no
      historico da politica - a auditoria perdia a linha "Aplicativos(novo)".
      Aqui devolvemos o item ORIGINAL com os dois campos sobrescritos.

   2. `KIOSK` estava na lista de tipos do `tipoAplicativoLista` do topo do arquivo
      (nao usada) e fora da lista do Autocomplete (usada). A lista efetiva estava
      certa: quem define o app de quiosque e o seletor de Kiosk da politica, que
      alem de marcar o app tambem devolve o anterior para FORCE_INSTALLED. Deixar
      KIOSK aqui permitiria dois apps em modo quiosque - o Google aceita um so.
      Mantemos KIOSK fora, e dizemos por que.

   3. `console.log('item:::', item)` a cada render.

   A remocao fica aqui, e nao na tabela, porque e onde o usuario ja esta olhando o
   aplicativo que quer tirar - e sai com rotulo, nao com um icone de lixeira solto.
   ============================================================================ */

export type AplicativoPolitica = {
  packageName?: string;
  name?: string;
  codigo?: number;
  installType?: string;
  autoUpdateMode?: string;
  novo?: boolean;
};

export const TIPOS_INSTALACAO = [
  { valor: 'INSTALL_TYPE_UNSPECIFIED', rotulo: 'Sem especificar' },
  { valor: 'PREINSTALLED', rotulo: 'Pre-instalado' },
  { valor: 'FORCE_INSTALLED', rotulo: 'Forçar instalação' },
  { valor: 'BLOCKED', rotulo: 'Bloqueado' },
  { valor: 'AVAILABLE', rotulo: 'Disponivel para download' },
  { valor: 'REQUIRED_FOR_SETUP', rotulo: 'Obrigatorio na instalação' },
];

/** Existe so no rotulo das tabelas: o valor e gravado pelo seletor de Kiosk. */
export const TIPO_KIOSK = { valor: 'KIOSK', rotulo: 'Modo Kiosk' };

export const MODOS_ATUALIZACAO = [
  { valor: 'AUTO_UPDATE_DEFAULT', rotulo: 'Padrão' },
  { valor: 'AUTO_UPDATE_POSTPONED', rotulo: 'Sem atualizar(90 dias depois)' },
  { valor: 'AUTO_UPDATE_HIGH_PRIORITY', rotulo: 'Alta prioridade' },
];

/** Textos do Google para cada tipo. Ficam aqui porque a tela e o modal usam os mesmos. */
export const DESCRICAO_TIPO: Record<string, string> = {
  INSTALL_TYPE_UNSPECIFIED: 'Não especificado. O padrão é AVAILABLE.',
  PREINSTALLED: 'O app é instalado automaticamente e pode ser removido pelo usuário.',
  FORCE_INSTALLED: 'O app é instalado automaticamente e não pode ser removido pelo usuário.',
  BLOCKED:
    'O app está bloqueado e não pode ser instalado. Se o app tiver sido instalado com base em uma política anterior, ele será desinstalado.',
  AVAILABLE: 'O app está disponível para instalação.',
  REQUIRED_FOR_SETUP:
    'O app é instalado automaticamente e não pode ser removido pelo usuário. Isso impedirá a configuração até que a instalação seja concluída.',
  KIOSK:
    'O aplicativo é instalado automaticamente no modo quiosque: ele é definido como a intent inicial desejada e incluído na lista de permissões do modo de tarefa de bloqueio. A configuração do dispositivo não será concluída até que o app seja instalado. Após a instalação, os usuários não poderão remover o app. Só é possível definir este installType para um app por política. Quando ela estiver presente na política, a barra de status será desativada automaticamente.',
};

export function rotuloTipo(valor: unknown) {
  if (!valor) return ' - ';
  const achado = [...TIPOS_INSTALACAO, TIPO_KIOSK].find((t) => t.valor === valor);
  return achado?.rotulo || String(valor);
}

export function rotuloAtualizacao(valor: unknown) {
  if (!valor) return ' - ';
  return MODOS_ATUALIZACAO.find((m) => m.valor === valor)?.rotulo || String(valor);
}

export default function ModalAplicativoPolitica({
  aberto,
  item,
  onFechar,
  onSalvar,
  onRemover,
}: {
  aberto: boolean;
  item: AplicativoPolitica | null;
  onFechar: () => void;
  onSalvar: (item: AplicativoPolitica) => void;
  onRemover: (item: AplicativoPolitica) => void;
}) {
  const [tipo, setTipo] = React.useState('FORCE_INSTALLED');
  const [atualizacao, setAtualizacao] = React.useState('AUTO_UPDATE_DEFAULT');
  const [confirmarRemocao, setConfirmarRemocao] = React.useState(false);

  React.useEffect(() => {
    if (!aberto || !item) return;
    setTipo(item.installType || 'FORCE_INSTALLED');
    setAtualizacao(item.autoUpdateMode || 'AUTO_UPDATE_DEFAULT');
    setConfirmarRemocao(false);
  }, [aberto, item?.packageName]);

  if (!aberto || !item) return null;

  // O app de quiosque tem tipo KIOSK, que nao esta na lista. Trocar o tipo dele
  // aqui tiraria o quiosque sem que o seletor de Kiosk soubesse - avisamos em vez
  // de deixar o usuario desmontar a configuracao sem perceber.
  const ehAppKiosk = item.installType === 'KIOSK';

  return (
    <Modal
      aberto={aberto}
      titulo={item.name || item.packageName || 'Aplicativo'}
      descricao={<span className="tabular-nums">{item.packageName}</span>}
      largura="max-w-[520px]"
      onFechar={onFechar}
      rodape={
        <>
          <button
            type="button"
            onClick={() => setConfirmarRemocao(true)}
            className="mr-auto h-9 rounded-lg border border-danger px-3 text-[12.5px] font-semibold text-danger"
          >
            Remover da política
          </button>
          <BotaoSecundario onClick={onFechar}>Fechar</BotaoSecundario>
          <BotaoPrimario
            onClick={() => onSalvar({ ...item, installType: tipo, autoUpdateMode: atualizacao })}
          >
            Aplicar na lista
          </BotaoPrimario>
        </>
      }
    >
      {confirmarRemocao ? (
        <div className="rounded-lg border border-danger bg-danger-soft p-3">
          <p className="text-[12.5px] font-semibold text-ink">
            Remover <strong>{item.name}</strong> da lista de aplicativos desta política?
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            A remoção só vale quando a política for salva. O aparelho desinstala o app apenas se
            &quot;Desistalar aplicativos&quot; NÃO estiver marcado.
          </p>
          <div className="mt-3 flex gap-2">
            <BotaoSecundario onClick={() => setConfirmarRemocao(false)}>Cancelar</BotaoSecundario>
            <button
              type="button"
              onClick={() => onRemover(item)}
              className="h-9 rounded-lg bg-danger px-3 text-[12.5px] font-bold text-white"
            >
              Remover
            </button>
          </div>
        </div>
      ) : (
        <>
          {ehAppKiosk && (
            <div className="rounded-lg border border-warn bg-warn-soft p-3 text-[12px] text-ink">
              Este é o aplicativo do <strong>modo Kiosk</strong>. Para trocá-lo, use o seletor de
              Kiosk no cartão &quot;Modo Kiosk&quot; — ele devolve o app anterior para
              &quot;Forçar instalação&quot; automaticamente.
            </div>
          )}

          <Campo rotulo="Tipo de instalação" dica={DESCRICAO_TIPO[tipo]}>
            <select
              className={classesEntrada}
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              disabled={ehAppKiosk}
            >
              {ehAppKiosk && <option value="KIOSK">{TIPO_KIOSK.rotulo}</option>}
              {TIPOS_INSTALACAO.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Atualização">
            <select
              className={classesEntrada}
              value={atualizacao}
              onChange={(e) => setAtualizacao(e.target.value)}
            >
              {MODOS_ATUALIZACAO.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </>
      )}
    </Modal>
  );
}
